import React, { useState } from "react";
import { icon, IconComponent } from "../../icon-component";
import styles from "./CustomChips.module.css";
import { CustomTooltip } from "../../tooltip";

type ChipColor = "info" | "success" | "warning" | "error" | "neutral";
type ChipSize = "sm" | "md";
type ChipIcon = React.ComponentProps<typeof IconComponent>["icon"];
type IconSize = React.ComponentProps<typeof IconComponent>["size"];

const iconSizes: Record<ChipSize, IconSize> = {
	sm: "xs",
	md: "sm",
};

const endIconSizes: Record<ChipSize, IconSize> = {
	sm: "2xs",
	md: "xs",
};

interface CustomChipsProps {
	readonly label?: string;
	readonly startIcon?: ChipIcon | null;
	readonly endIcon?: ChipIcon;
	readonly isActive?: boolean;
	readonly color?: ChipColor;
	readonly size?: ChipSize;
	readonly className?: string;
	readonly closable?: boolean;
	readonly onClose?: ((event: React.MouseEvent<HTMLButtonElement>) => void) | null;
	readonly onClick?: (() => void) | null;
}

export default function CustomChips({
	label = "",
	startIcon = null,
	endIcon = icon.faTimes,
	color = "info",
	size = "md",
	className = "",
	closable = false,
	isActive = false,
	onClose = null,
	onClick = null,
}: CustomChipsProps) {
	const [isVisible, setIsVisible] = useState(true);

	if (!isVisible) {
		return null;
	}

	const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		setIsVisible(false);
		onClose?.(event);
	};

	const handleClick = () => {
		onClick?.();
	};

	return (
		<CustomTooltip showWhenOverflow title={label}>
			<div
				className={`${styles.chip} ${className}`}
				data-color={color}
				data-is-active={isActive}
				data-size={size}
				role="presentation"
				onClick={handleClick}
			>
				{startIcon && (
					<IconComponent
						className={styles.startIcon}
						color="currentColor"
						icon={startIcon}
						size={iconSizes[size]}
					/>
				)}
				<span data-truncate className={styles.label}>
					{label}
				</span>
				{closable && (
					<button className={styles.endIconBtn} type="button" onClick={handleClose}>
						<IconComponent
							className={styles.endIcon}
							color="currentColor"
							icon={endIcon}
							size={endIconSizes[size]}
						/>
					</button>
				)}
			</div>
		</CustomTooltip>
	);
}
